import {
  type EvidenceTypeEnum,
  getApiTradeV1SiteMaterialsEvidenceListQueryKey,
  useApiTradeV1SiteMaterialsEvidenceListSuspense,
  useQueryClient,
} from 'api';
import { EvidenceUploadButton, useSiteMaterial } from 'features';
import { useTranslation } from 'i18n';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ActionRow,
  Button,
  Grid,
  MediaDialog,
  Page,
  PhotoEvidenceItem,
  PhotoEvidenceList,
  Section,
  Typography,
  useBreakpoint,
} from 'ui';

import { SiteHeader } from '../../components/SiteHeader/SiteHeader';

export const Evidence = ({ evidenceType }: { evidenceType: EvidenceTypeEnum }) => {
  const { t } = useTranslation('orders');
  const { isMobile } = useBreakpoint();
  const queryClient = useQueryClient();
  const { data: siteMaterial } = useSiteMaterial();
  const { data } = useApiTradeV1SiteMaterialsEvidenceListSuspense(String(siteMaterial.id), {
    page_size: 10000,
    type: evidenceType,
  });

  const carouselMedia = data.results.map((file) => String(file.file));
  const [selectedImage, setSelectedImage] = useState<string>('');

  const handleSelect = (src: string) => {
    const relatedMedia = data.results.find((photo) => photo.file === src || photo.preview === src);
    setSelectedImage(relatedMedia?.file || '');
  };

  if (!data) {
    return <Typography>{t('evidencePage.materialNotFound')}</Typography>;
  }

  return (
    <>
      <Page>
        <Page.Title title={t('evidencePage.pageTitle')} />
        <Page.Divider />
        <Grid container>
          <Grid
            size={{
              md: 4,
              xs: 12,
            }}
          >
            <Section title={t('evidencePage.sections.siteDetails')}>
              <SiteHeader siteMaterial={siteMaterial} />
            </Section>
          </Grid>
          <Grid
            size={{
              md: 8,
              xs: 12,
            }}
          >
            <Section
              title={t('evidencePage.sections.evidence')}
              action={
                <ActionRow>
                  <Button size="large" fullWidth variant="secondary" component={Link} to="edit">
                    {t('evidencePage.actions.edit')}
                  </Button>
                  <EvidenceUploadButton
                    evidenceType={evidenceType}
                    parentId={String(siteMaterial.id)}
                    mode={'site-material'}
                    onSuccess={() => {
                      queryClient.invalidateQueries({
                        queryKey: getApiTradeV1SiteMaterialsEvidenceListQueryKey(String(siteMaterial.id)),
                      });
                    }}
                  />
                </ActionRow>
              }
            >
              <PhotoEvidenceList
                openOnClick
                slotProps={{
                  imageList: {
                    cols: isMobile ? 3 : 5,
                  },
                }}
                evidencePhotos={data.results.map(({ file, preview }) => ({
                  src: (preview || file) ?? '',
                }))}
                handleClick={(photo: PhotoEvidenceItem) => handleSelect(photo.src)}
              />
            </Section>
          </Grid>
        </Grid>
      </Page>
      <MediaDialog
        open={Boolean(selectedImage)}
        selectedMedium={selectedImage}
        onClose={() => setSelectedImage('')}
        media={carouselMedia}
        handleNavigate={setSelectedImage}
        imageCount={carouselMedia.length}
      />
    </>
  );
};
